// Modal dialog controller (ported from common/modal_dialog.js).
//
// Markup contract:
//   <button class="modal_dialog" rel="#some_id">Open</button>
//   <div id="some_id" class="modal_form">...form...</div>
//
// Behavior:
//   - Click trigger → show modal + backdrop, lock body scroll
//   - Click backdrop / press Esc / submit form / click a .modal-cancel or
//     [data-modal-close] → close
//   - A Cancel button is auto-injected into modal forms that don't already
//     carry a .modal-actions row.
//
// Singleton state is module-scoped so it survives Turbo navigations.

let backdrop: HTMLElement | null = null
let current: HTMLElement | null = null

function ensureBackdrop(): HTMLElement {
  if (!backdrop || !backdrop.isConnected) {
    backdrop = document.createElement("div")
    backdrop.className = "modal-backdrop"
    backdrop.addEventListener("click", closeModal)
    document.body.appendChild(backdrop)
  }
  return backdrop
}

function closeModal(): void {
  if (!current) return
  current.classList.remove("is-open")
  current = null
  ensureBackdrop().classList.remove("is-open")
  document.body.classList.remove("modal-open")
}

function openModal(modal: HTMLElement | null): void {
  if (!modal) return
  if (current) closeModal()
  current = modal
  ensureBackdrop().classList.add("is-open")
  modal.classList.add("is-open")
  document.body.classList.add("modal-open")
  // Defer focus so the dialog is painted/open first.
  setTimeout(() => {
    modal.querySelector<HTMLInputElement>("input[type=text]")?.focus()
  }, 0)
}

// ----- Delegated handlers — bind once, survive navigations -----

document.addEventListener("click", (e) => {
  const target = e.target as HTMLElement

  const trigger = target.closest<HTMLElement>(".modal_dialog")
  if (trigger) {
    const rel = trigger.getAttribute("rel")
    if (rel) {
      e.preventDefault()
      openModal(document.querySelector<HTMLElement>(rel))
    }
    return
  }

  if (target.closest(".modal-cancel, [data-modal-close]")) {
    e.preventDefault()
    closeModal()
  }
})

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && current) closeModal()
})

document.addEventListener("submit", (e) => {
  const form = e.target as HTMLElement
  if (form.matches("form") && form.closest(".modal_form")) closeModal()
})

// Per-page: inject a Cancel button into each modal form that lacks one.
// Idempotent — the .modal-actions guard means re-running on every
// turbo:load is harmless.
function injectCancelButtons(): void {
  document.querySelectorAll<HTMLFormElement>(".modal_form form").forEach((form) => {
    if (form.querySelector(".modal-actions")) return
    const cancel = document.createElement("button")
    cancel.type = "button"
    cancel.className = "modal-cancel"
    cancel.textContent = "Cancel"
    const actions = document.createElement("div")
    actions.className = "modal-actions"
    const submits = form.querySelectorAll<HTMLElement>("input[type=submit], button[type=submit]")
    const submit = submits[submits.length - 1]
    if (submit) {
      submit.before(actions)
      actions.append(cancel, submit)
    } else {
      actions.append(cancel)
      form.append(actions)
    }
  })
}

document.addEventListener("turbo:load", injectCancelButtons)

// Close any open modal before Turbo snapshots the page — otherwise the
// cached copy carries `.is-open` + body `modal-open` (scroll locked), and
// restoring it on back-navigation shows a stuck modal whose backdrop has
// no click listener and whose module `current` state is gone.
document.addEventListener("turbo:before-cache", closeModal)
